import type { SearchResultContentSource, SearchResultContentType } from '../../types';
import { useEffect, useMemo, useState } from 'react';
import { Button, CheckboxGroup, Col, ModalBody, ModalHeader, Row, Stack, SubHeading } from '@workramp/ui';
import { useTheme } from 'styled-components';
import { I18n } from '~/common/explicit-exports';
import isFeatureEnabled from '~/components/common/is-feature-enabled';
import { FullScreenModal, MobilePadding } from '~/features/common/styles';
import { hideIntercomButton, showIntercomButton } from '~/utils';
import { MixPanel } from '~/common';
import { useGetSkills } from '~/elc/features/skills';
import { LearnerSearchMixpanelEvents, SearchResultContentSourceTypes, SearchResultContentTypes } from '../../constants';
import { AdvancedSearchContentTypeOptions } from '../AdvancedSearchContentTypeOptions';
import { useSearch } from '../../providers';
import { UpdateDate } from '../../types';
import { AdvancedSearchResetFilterButton } from '../AdvancedSearchResetFilterButton';
import { AdvancedSearchFilterUpdatedDateRadioGroup } from '../AdvancedSearchFilters/AdvancedSearchFilterUpdatedDateRadioGroup';
import { UpdatedByAutocomplete } from '../AdvancedSearchFilters/UpdatedByAutocomplete';
import { StyledModalFooter } from './AdvancedSearchFilterModal.styled';
import { AdvancedSearchFilterModalAutocompleteFilter } from './AdvancedSearchFilterModalAutocompleteFilter';

export const AdvancedSearchFilterModal = () => {
  const FILTER = I18n.t('js.learner.advanced_search.filters.title');
  const RESET = I18n.t('js.learner.advanced_search.filters.reset');
  const APPLY = I18n.t('js.learner.advanced_search.filters.apply');
  const TAGS = I18n.t('js.learner.advanced_search.filters.tags');
  const UPDATED = I18n.t('js.learner.advanced_search.filters.updated');
  const TYPES = I18n.t('js.learner.advanced_search.filters.type');
  const SKILLS = I18n.t('js.learner.advanced_search.filters.skills');

  const [isOpen, setIsOpen] = useState(false);
  const {
    contentTypes,
    setContentTypes,
    contentSources,
    setContentSources,
    filterUpdatedDate,
    updateFilterUpdatedDate,
    selectedTags,
    setSelectedTags,
    availableTags,
    setTagSearchTerm,
    tagSearchTerm,
    selectedSkills,
    setSelectedSkills,
    availableSkills,
    setSkillSearchTerm,
    skillSearchTerm,
    setSelectedUpdatedByUserIds,
    selectedUpdatedByUserIds,
  } = useSearch();
  const [contentTypeOptionsSelected, setContentTypeOptionsSelected] = useState(contentTypes);
  const [contentSourceOptionsSelected, setContentSourceOptionsSelected] = useState(contentSources);
  const [updatedDate, setUpdatedDate] = useState(filterUpdatedDate);
  const { colors } = useTheme();
  // "pending*" are the filters that are selected in the modal but not yet applied
  const [pendingTags, setPendingTags] = useState(selectedTags);
  const [pendingSkills, setPendingSkills] = useState(selectedSkills);
  const [pendingUpdatedByUserIds, setPendingUpdatedByUserIds] = useState(selectedUpdatedByUserIds);

  useEffect(() => {
    if (isOpen) {
      hideIntercomButton();
    } else {
      showIntercomButton();
    }
  }, [isOpen]);

  useEffect(() => {
    setUpdatedDate(filterUpdatedDate);
  }, [filterUpdatedDate]);

  useEffect(() => {
    setPendingTags(selectedTags);
  }, [selectedTags]);

  useEffect(() => {
    setPendingSkills(selectedSkills);
  }, [selectedSkills]);

  useEffect(() => {
    setPendingUpdatedByUserIds(selectedUpdatedByUserIds);
  }, [selectedUpdatedByUserIds]);

  useEffect(() => {
    setContentTypeOptionsSelected(contentTypes);
  }, [contentTypes]);

  useEffect(() => {
    setContentSourceOptionsSelected(contentSources);
  }, [contentSources]);

  const handleApplyClick = () => {
    setSelectedTags(pendingTags);
    setSelectedSkills(pendingSkills);
    setContentTypes(contentTypeOptionsSelected);
    setContentSources(contentSourceOptionsSelected);
    updateFilterUpdatedDate(updatedDate);
    setSelectedUpdatedByUserIds(pendingUpdatedByUserIds);
    setIsOpen(false);
  };

  const handleResetClick = () => {
    setSelectedTags([]);
    setSelectedSkills([]);
    setContentTypeOptionsSelected([]);
    setContentSourceOptionsSelected([]);
    setUpdatedDate(UpdateDate.ALL_TIME);
    setSelectedUpdatedByUserIds([]);
  };

  const setUnappliedFilterStateToCurrentlyApplied = () => {
    setPendingTags(selectedTags);
    setPendingSkills(selectedSkills);
    setContentTypeOptionsSelected(contentTypes);
    setContentSourceOptionsSelected(contentSources);
    setUpdatedDate(filterUpdatedDate);
    setPendingUpdatedByUserIds(selectedUpdatedByUserIds);
  };

  const onClose = () => {
    setIsOpen(false);
    setUnappliedFilterStateToCurrentlyApplied();
  };

  const areTypesDirty = useMemo(
    () => contentTypeOptionsSelected.sort().join(',') !== contentTypes.sort().join(','),
    [contentTypes, contentTypeOptionsSelected],
  );

  const areContentSourcesDirty = useMemo(
    () => contentSourceOptionsSelected.sort().join(',') !== contentSources.sort().join(','),
    [contentSources, contentSourceOptionsSelected],
  );

  const areTagsDirty = useMemo(
    () => selectedTags.sort().join(',') !== pendingTags.sort().join(','),
    [pendingTags, selectedTags],
  );

  const areSkillsDirty = useMemo(
    () => selectedSkills.sort().join(',') !== pendingSkills.sort().join(','),
    [pendingSkills, selectedSkills],
  );

  const isUpdatedDateDirty = useMemo(() => updatedDate !== filterUpdatedDate, [filterUpdatedDate, updatedDate]);

  const areSelectedUpdatedByUserIdsDirty = useMemo(
    () => selectedUpdatedByUserIds.sort().join(',') !== pendingUpdatedByUserIds.sort().join(','),
    [pendingUpdatedByUserIds, selectedUpdatedByUserIds],
  );

  const isDirty = useMemo(
    () =>
      areTypesDirty ||
      areContentSourcesDirty ||
      areTagsDirty ||
      isUpdatedDateDirty ||
      areSelectedUpdatedByUserIdsDirty ||
      areSkillsDirty,
    [
      areTagsDirty,
      areContentSourcesDirty,
      areTypesDirty,
      isUpdatedDateDirty,
      areSelectedUpdatedByUserIdsDirty,
      areSkillsDirty,
    ],
  );

  const skillsQuery = useGetSkills(undefined, 1);
  const companyHasAvailableSkills = useMemo(() => skillsQuery.data?.skills.length > 0, [skillsQuery.data?.skills]);

  const handleTypeAndSourceChange = (selectedOptions: (SearchResultContentType | SearchResultContentSource)[]) => {
    const selectedTypes = selectedOptions.filter((so) => Object.values<string>(SearchResultContentTypes).includes(so));

    setContentTypeOptionsSelected(selectedTypes as SearchResultContentType[]);
    const selectedSources = selectedOptions.filter((so) =>
      Object.values<string>(SearchResultContentSourceTypes).includes(so),
    );

    setContentSourceOptionsSelected(selectedSources as SearchResultContentSource[]);
    MixPanel.track(LearnerSearchMixpanelEvents.LEARNER_ADVANCED_SEARCH_SELECTS_TYPE_FILTER, { selectedOptions });
  };

  const typesAndSources: (SearchResultContentType | SearchResultContentSource)[] = [
    ...contentTypeOptionsSelected,
    ...contentSourceOptionsSelected,
  ];

  return (
    <>
      <AdvancedSearchResetFilterButton onOpen={() => setIsOpen(true)} />
      <FullScreenModal isOpen={isOpen} onClose={onClose} zIndex={1041}>
        <MobilePadding />
        <ModalHeader title={FILTER} />
        <ModalBody>
          <Stack size={4}>
            {isFeatureEnabled('fy25_q3_elc_skills_in_search') && (
              <>
                <SubHeading level="md" color={colors.secondary[700]}>
                  {SKILLS}
                </SubHeading>
                <AdvancedSearchFilterModalAutocompleteFilter
                  selectedObjects={pendingSkills}
                  setSelectedObjects={setPendingSkills}
                  availableObjects={availableSkills}
                  setObjectSearchTerm={setSkillSearchTerm}
                  objectSearchTerm={skillSearchTerm}
                  mixpanelEvent={LearnerSearchMixpanelEvents.LEARNER_ADVANCED_SEARCH_SELECTS_SKILL}
                  isDisabled={!companyHasAvailableSkills}
                  data-testid="advanced-search-skills-filter"
                />
              </>
            )}
            <SubHeading level="md" color={colors.secondary[700]}>
              {TAGS}
            </SubHeading>
            <AdvancedSearchFilterModalAutocompleteFilter
              selectedObjects={pendingTags}
              setSelectedObjects={setPendingTags}
              availableObjects={availableTags}
              setObjectSearchTerm={setTagSearchTerm}
              objectSearchTerm={tagSearchTerm}
              mixpanelEvent={LearnerSearchMixpanelEvents.LEARNER_ADVANCED_SEARCH_SELECTS_TAG}
            />
            <SubHeading level="md" color={colors.secondary[700]}>
              {TYPES}
            </SubHeading>
            <CheckboxGroup hideLabel label="Filter Types" value={typesAndSources} onChange={handleTypeAndSourceChange}>
              <AdvancedSearchContentTypeOptions />
            </CheckboxGroup>
            <SubHeading level="md" color={colors.secondary[700]}>
              {UPDATED}
            </SubHeading>
            <AdvancedSearchFilterUpdatedDateRadioGroup
              filterUpdatedDate={updatedDate}
              setFilterUpdatedDate={setUpdatedDate}
            />
            <UpdatedByAutocomplete
              selectedUpdatedByUserIds={pendingUpdatedByUserIds}
              setSelectedUpdatedByUserIds={setPendingUpdatedByUserIds}
            />
          </Stack>
        </ModalBody>
        <StyledModalFooter
          actions={
            <Row justify="flex-end" gapX={2}>
              <Col xs="auto">
                <Button data-testid="button-reset" color="secondary" variant="text" onPress={handleResetClick}>
                  {RESET}
                </Button>
              </Col>
              <Col xs="auto">
                <Button data-testid="button-apply" isDisabled={!isDirty} onPress={handleApplyClick} variant="solid">
                  {APPLY}
                </Button>
              </Col>
            </Row>
          }
        />
      </FullScreenModal>
    </>
  );
};
